import { inject } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  Route,
  Router,
  UrlSegment,
  UrlTree,
} from '@angular/router'

import type { FeatureKey } from '@app/features/feature-keys.generated'
import { FeatureService } from '@app/services/feature.service'

/**
 * Route-level feature gate (functional guards — the class-based `CanActivate`
 * and `CanMatch` interfaces are deprecated since Angular 14.2 / 15).
 *
 * - `featureCanMatch` blocks Angular from even matching (and downloading) a
 *   lazy module whose `data.feature` is off for the active tenant. The JS
 *   chunk is never fetched. Use on `loadChildren` routes.
 * - `featureCanActivate` redirects manual URL navigation to root when the
 *   feature is off. Use for eager routes if any are ever feature-gated.
 *
 * Both read `data.feature` and delegate to `FeatureService.has(...)` —
 * a single Toggle Point per route.
 */
function check(feature: FeatureKey | undefined): boolean | UrlTree {
  if (!feature) {
    return true
  }
  const features = inject(FeatureService)
  if (features.has(feature)) {
    return true
  }
  // Send unauthorized URL access to root. For canMatch this also means the
  // route is unmatched so its lazy bundle never downloads.
  return inject(Router).parseUrl('/')
}

export const featureCanActivate: CanActivateFn = (route: ActivatedRouteSnapshot) =>
  check(route.data?.['feature'] as FeatureKey | undefined)

export const featureCanMatch: CanMatchFn = (route: Route, _segments: UrlSegment[]) =>
  check(route.data?.['feature'] as FeatureKey | undefined)
